<template>
    <div v-if="shouldLoad">
        <UpsellPage v-if="displayUpsellPage"></UpsellPage>

        <aside
            v-click-outside="closeSidebar"
            :class="{ 'sidebar--displayed': sidebarDisplayed }"
            class="sidebar"
        >
            <div class="logo">
                <img
                    alt="goTenna Pro Management Portal"
                    src="/images/logo.png"
                />
                <span
                    v-if="$store.getters['environmentNameSafe']"
                    class="environment-name"
                >
                    {{ $store.getters['environmentNameSafe'] }}
                </span>
            </div>

            <nav v-if="organizationSlug">
                <div
                    v-if="user && !user.organization_id"
                    class="sidebar__section"
                >
                    <ul>
                        <li>
                            <router-link
                                data-test-id="nav-organizations"
                                tag="a"
                                to="/organizations"
                            >
                                <span class="icon-chevron-left"></span> Back to Admin Portal
                            </router-link>
                        </li>
                    </ul>
                </div>

                <div class="sidebar__section">
                    <h3>General</h3>
                    <ul>
                        <li v-if="can('view dashboard')">
                            <router-link
                                id="nav_dashboard"
                                :to="`/${organizationSlug}/dashboard`"
                                data-test-id="nav-dashboard"
                                tag="a"
                            >
                                Dashboard
                            </router-link>
                        </li>
                        <li>
                            <a
                                :href="knowledgeBaseUrl"
                                target="_blank"
                                >Knowledge Base<span class="external-link-icon">&#x2197;</span></a
                            >
                        </li>
                        <li v-if="can('view frequency sets')">
                            <router-link
                                id="nav_frequency_sets"
                                :to="`/${organizationSlug}/frequency-sets`"
                                data-test-id="nav-frequency-sets"
                                tag="a"
                            >
                                Frequency Sets
                            </router-link>
                        </li>
                        <li v-if="can('view apps')">
                            <router-link
                                id="nav_app_repository"
                                :to="`/${organizationSlug}/app-repository`"
                                data-test-id="nav-app-repo"
                                tag="a"
                            >
                                App Repository
                            </router-link>
                        </li>
                        <li
                            v-if="
                                organization &&
                                organizationFeatureEnabled('ALLOW_CONFIGURATION_SETS') &&
                                can('view encryption keys')
                            "
                        >
                            <router-link
                                id="nav_encryption_keys"
                                :to="`/${organizationSlug}/encryption-keys`"
                                data-test-id="nav-encryption-keys"
                                tag="a"
                            >
                                Encryption Keys
                            </router-link>
                        </li>
                        <li v-if="organization && organizationFeatureEnabled('ALLOW_WORKFLOWS') && workflowsAppUrl">
                            <a
                                :href="workflowsAppUrl"
                                data-test-id="nav-workflows"
                                target="_blank"
                                >Workflows<span class="external-link-icon">&#x2197;</span></a
                            >
                        </li>
                        <li
                            v-if="
                                organization &&
                                can('view assets') &&
                                organizationFeatureEnabled('ALLOW_RELAYS') &&
                                featureFlagEnabled(featureFlags.ASSETS_PAGE_FEATURE_ENABLED)
                            "
                        >
                            <router-link
                                id="nav_relays"
                                :to="`/${organizationSlug}/assets`"
                                data-test-id="nav-assets"
                                tag="a"
                            >
                                Assets<span class="beta-tag">BETA</span>
                            </router-link>
                        </li>
                        <li v-if="organization && organizationFeatureEnabled('ALLOW_RELAYS') && can('view assets')">
                            <router-link
                                id="nav_relays"
                                :to="`/${organizationSlug}/relays`"
                                data-test-id="nav-relays"
                                tag="a"
                            >
                                Relays
                            </router-link>
                        </li>
                        <li
                            v-if="
                                organization &&
                                organizationFeatureEnabled('ALLOW_CONFIGURATION_SETS') &&
                                can('view configuration sets') &&
                                featureFlagEnabled(featureFlags.CONFIGURATION_SETS_FEATURE_ENABLED)
                            "
                        >
                            <router-link
                                id="nav_configuration_sets"
                                :to="`/${organizationSlug}/configuration-sets`"
                                data-test-id="nav-configuration-sets"
                                tag="a"
                            >
                                Configuration&nbsp;Sets
                            </router-link>
                        </li>
                        <li
                            v-if="
                                can('view diagnostic logs') && featureFlagEnabled(featureFlags.DIAGNOSTIC_LOGS_ENABLED)
                            "
                        >
                            <router-link
                                id="nav_diagnostic_logs"
                                :to="`/${organizationSlug}/diagnostic-logs`"
                                data-test-id="nav-diagnostic-logs"
                                tag="a"
                            >
                                Diagnostic Logs
                            </router-link>
                        </li>
                    </ul>
                </div>

                <div
                    v-if="canAny(['view users', 'view missions', 'view user groups'])"
                    class="sidebar__section"
                >
                    <h3>User</h3>
                    <ul>
                        <li v-if="can('view users')">
                            <router-link
                                id="nav_users"
                                :to="`/${organizationSlug}/users`"
                                data-test-id="nav-users"
                                tag="a"
                            >
                                Users
                            </router-link>
                        </li>
                        <li v-if="organization && organizationFeatureEnabled('ALLOW_MAP') && can('view missions')">
                            <router-link
                                id="nav_map"
                                :to="`/${organizationSlug}/user-map`"
                                data-test-id="nav-user-map"
                                tag="a"
                            >
                                Map<span class="beta-tag">BETA</span>
                            </router-link>
                        </li>
                        <li v-if="can('view user groups')">
                            <router-link
                                id="nav_user_groups"
                                :to="`/${organizationSlug}/user-groups`"
                                data-test-id="nav-user-groups"
                                tag="a"
                            >
                                Groups
                            </router-link>
                        </li>
                    </ul>
                </div>

                <premium-nav-section v-if="can('view license information')" />
            </nav>

            <nav v-else>
                <div class="sidebar__section">
                    <h3>General</h3>
                    <ul>
                        <li v-if="can('view organizations')">
                            <router-link
                                data-test-id="nav-organizations"
                                tag="a"
                                to="/organizations"
                                >Organizations
                            </router-link>
                        </li>
                        <li v-if="can('view mobile features')">
                            <router-link
                                data-test-id="nav-features"
                                tag="a"
                                to="/mobile-features"
                                >Mobile Features
                            </router-link>
                        </li>
                        <li v-if="can('view apps')">
                            <router-link
                                id="nav_app_repository"
                                :to="`/app-repository`"
                                data-test-id="nav-app-repo"
                                tag="a"
                            >
                                App Repository
                            </router-link>
                        </li>
                        <li v-if="can('view users') && can('search across organizations')">
                            <router-link
                                id="nav_user_information"
                                :to="`/users`"
                                data-test-id="nav-user-information"
                                tag="a"
                            >
                                User Information
                            </router-link>
                        </li>
                        <li v-if="can('view search indexes')">
                            <router-link
                                id="nav_search_indexes"
                                :to="`/search`"
                                data-test-id="nav-search-indexes"
                                tag="a"
                            >
                                Search Indexes
                            </router-link>
                        </li>
                        <li v-if="can('view global feature flags')">
                            <router-link
                                id="nav_feature_flags"
                                :to="`/feature-flags`"
                                data-test-id="nav-feature-flags"
                                tag="a"
                            >
                                Feature Flags
                            </router-link>
                        </li>
                    </ul>
                </div>

                <div
                    v-if="
                        canAny(['view firmware', 'view user agents', 'view device whitelist', 'view products']) ||
                        (can('list all organizations') && can('view radios'))
                    "
                    class="sidebar__section"
                >
                    <h3>Devices</h3>
                    <ul>
                        <li v-if="can('view products')">
                            <router-link
                                data-test-id="nav-products"
                                tag="a"
                                to="/products"
                                >Products
                            </router-link>
                        </li>
                        <li v-if="can('view firmware')">
                            <router-link
                                data-test-id="nav-firmware"
                                tag="a"
                                to="/firmware"
                                >Firmware
                            </router-link>
                        </li>
                        <li v-if="can('view firmware')">
                            <router-link
                                data-test-id="nav-compatibility"
                                tag="a"
                                to="/compatibility"
                            >
                                Compatibility
                            </router-link>
                        </li>
                        <li v-if="can('view device whitelist')">
                            <router-link
                                data-test-id="nav-device-whitelist"
                                tag="a"
                                to="/device-whitelist"
                                >Device Whitelist
                            </router-link>
                        </li>
                        <li v-if="can('view radios') && can('list all organizations')">
                            <router-link
                                data-test-id="nav-radios"
                                tag="a"
                                to="/radios/summary"
                                >Radios
                            </router-link>
                        </li>
                    </ul>
                </div>

                <div
                    v-if="can('view roles') && can('view permissions')"
                    class="sidebar__section"
                >
                    <h3>Permissions</h3>
                    <ul>
                        <li>
                            <router-link
                                data-test-id="nav-roles"
                                tag="a"
                                to="/roles"
                                >Roles
                            </router-link>
                        </li>
                        <li>
                            <router-link
                                data-test-id="nav-permissions"
                                tag="a"
                                to="/permissions"
                                >Permissions
                            </router-link>
                        </li>
                    </ul>
                </div>

                <div
                    v-if="can('view api documentation')"
                    class="sidebar__section"
                >
                    <h3>Developers</h3>
                    <ul>
                        <li>
                            <router-link
                                id="nav-dev"
                                :to="`/developers`"
                                data-test-id="nav-developers"
                                tag="a"
                            >
                                API Reference
                            </router-link>
                        </li>
                    </ul>
                </div>
            </nav>
        </aside>

        <div
            :class="{ 'sidebar--displayed': sidebarDisplayed }"
            class="content"
        >
            <header class="content__header">
                <section class="support">
                    <a
                        class="icon-menu support__toggle-sidebar"
                        href="#"
                        @click.prevent="toggleSidebar"
                    ></a>
                </section>

                <section
                    v-if="user"
                    class="user"
                >
                    <span
                        v-if="organization"
                        class="user__organization"
                        >{{ organization.name }}</span
                    >
                    <account-dropdown :user="user" />
                </section>
            </header>

            <section class="content__body">
                <router-view />
            </section>
        </div>
    </div>
</template>

<script>
import AccountDropdown from '@/components/AccountDropdown.vue'
import PremiumNavSection from '@/components/PremiumNavSection.vue'
import UpsellPage from '@/components/UpsellPage/index.vue'
import needsOrganizationSlug from '@/mixins/needsOrganizationSlug'
import Permissions from '@/mixins/permissions'
import featureFlags from '@/constants/feature-flags'
import knowledgeBase from '@/constants/knowledge-base'

export default {
    components: {
        AccountDropdown,
        UpsellPage,
        PremiumNavSection,
    },

    mixins: [needsOrganizationSlug, Permissions],

    props: ['user', 'organization'],

    data() {
        return {
            featureFlags,
            sidebarDisplayed: false,
            shouldLoad: true,
            knowledgeBaseUrl: knowledgeBase.HOME,
            workflowsAppUrl: window.envVars?.WORKFLOWS_APP_URL,
        }
    },

    computed: {
        displayUpsellPage() {
            return this.$store.state.mission.displayUpsellPage
        },
    },

    watch: {
        '$route.path': {
            async handler() {
                await this.refreshUser()
                if (this.backedOutOfOrganization()) {
                    this.$store.dispatch('clearOrganization')
                } else if (this.drilledIntoOrganization()) {
                    this.getOrganization()
                }
            },
            flush: 'post',
        },
    },

    async created() {
        await this.refreshUser(true)
        if (this.organizationSlug && this.can('view organizations')) {
            this.getOrganization()
        }
    },

    methods: {
        closeSidebar(e) {
            // Sometimes e.target.className can be an object and not a string, which causes the indexOf() call to fail
            if (e.target.className instanceof String && e.target.className.indexOf('support__toggle-sidebar') === -1) {
                this.sidebarDisplayed = false
            }
        },

        toggleSidebar() {
            this.sidebarDisplayed = !this.sidebarDisplayed
        },

        backedOutOfOrganization() {
            return !this.organizationSlug && this.organization
        },

        drilledIntoOrganization() {
            return this.organizationSlug && !this.organization
        },

        getOrganization() {
            this.$store.dispatch('getOrganization', {
                slug: this.organizationSlug,
            })
        },

        async refreshUser(force = false) {
            if (this.$store.getters.shouldRefreshUser() || force) {
                this.shouldLoad = false
                await this.$store.dispatch('verifyUser')
                this.shouldLoad = true
            }
        },

        organizationFeatureEnabled(featureName) {
            return this.organization && this.organization.features.some((f) => f.feature === featureName)
        },

        featureFlagEnabled(flagName) {
            return this.$store.getters.hasFeatureFlag(flagName)
        },
    },
}
</script>

<style lang="scss" scoped>
.premium-section {
    & h3 {
        margin-bottom: 7px;
    }

    & div {
        margin-bottom: 25px;
    }
}

span.external-link-icon {
    margin-left: 7px;
    font-size: 0.7em;
    text-decoration: none !important;
    display: inline-block;
}
</style>
